import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { PipelineConnectionStatus, PipelineEventHandlers } from '../models/pipelineEvents';
import { followProject } from '../services/pipelineHub';

type Subscriber = MutableRefObject<PipelineEventHandlers>;
type Subscribe = (subscriber: Subscriber) => () => void;

const SubscribeContext = createContext<Subscribe | null>(null);
const StatusContext = createContext<PipelineConnectionStatus | null>(null);

/**
 * The page's one hub connection for `projectId`: it delivers the project's pipeline events to every
 * `usePipelineEvents` below it. A page that follows a project renders it once, above everything that subscribes.
 */
export function PipelineEventsProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const [subscribers] = useState(() => new Set<Subscriber>());
  const [status, setStatus] = useState<PipelineConnectionStatus>('connecting');

  const subscribe = useCallback<Subscribe>(
    (subscriber) => {
      subscribers.add(subscriber);
      return () => {
        subscribers.delete(subscriber);
      };
    },
    [subscribers],
  );

  useEffect(() => {
    if (!projectId) return;
    return followProject(projectId, {
      subscribers: () => Array.from(subscribers, (subscriber) => subscriber.current),
      onStatus: setStatus,
    });
  }, [projectId, subscribers]);

  return (
    <SubscribeContext.Provider value={subscribe}>
      <StatusContext.Provider value={status}>{children}</StatusContext.Provider>
    </SubscribeContext.Provider>
  );
}

/** Delivers the page's pipeline events to `handlers` while the calling component is mounted. */
export function usePipelineEvents(handlers: PipelineEventHandlers): void {
  const subscribe = useContext(SubscribeContext);
  if (!subscribe) throw new Error('usePipelineEvents needs a PipelineEventsProvider above it.');

  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });
  useEffect(() => subscribe(handlersRef), [subscribe]);
}

export function usePipelineConnectionStatus(): PipelineConnectionStatus {
  const status = useContext(StatusContext);
  if (!status) throw new Error('usePipelineConnectionStatus needs a PipelineEventsProvider above it.');
  return status;
}
